import {
  Col,
  Descriptions,
  Form,
  Input,
  Row,
  Typography,
} from 'antd';
import Moment from 'moment';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { capitalize } from 'utils/normalizers';
import ModalJSON from './ModalJSON';
import ModalObservaciones from './ModalObservaciones';

const ReadOnlyTabGeneral = ({
  ejecucion,
  origenesDeCredito,
  setEjecucion,
  mutable,
  estadosDeEjecucion,
  estatusDeEjecuciones,
}) => {
  const { actuacionesPae, tiposDePersonas } = useSelector((state) => state.catalogs);

  const lastEstadoDeEjecucion = useMemo(() => {
    const estadosConEstatusCompleto = estadosDeEjecucion.map((estado) => {
      const estatusCompleto = estatusDeEjecuciones
        .find((estatus) => estatus.id === estado.estatus_de_ejecucion);

      return {
        ...estado,
        estatus_de_ejecucion: estatusCompleto,
      };
    });

    const estadosVisibles = estadosConEstatusCompleto
      .filter((estado) => estado.estatus_de_ejecucion
      && !estado.estatus_de_ejecucion.solo_uso_interno)
      .at(-1);

    return estadosVisibles;
  }, [estadosDeEjecucion, estatusDeEjecuciones]);

  return (
    <>
      <br />
      <Col span={24}>
        <Descriptions
          layout="vertical"
          size="small"
          bordered
          column={{
            xs: 1, sm: 2, md: 3, lg: 6,
          }}
          labelStyle={{ fontWeight: 'bold' }}
        >
          <Descriptions.Item label="ID">
            {ejecucion?.id}
          </Descriptions.Item>
          <Descriptions.Item label="Folio">
            {ejecucion?.folio}
          </Descriptions.Item>
          <Descriptions.Item label="Origen del crédito">
            {origenesDeCredito.find((origen) => origen.id === ejecucion?.origen_del_credito)?.descripcion || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Fecha y hora de registro">
            {Moment(ejecucion?.estados_de_ejecucion?.[0]?.fecha_de_creacion).format('DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
          <Descriptions.Item label="Estatus de reporte moroso">
            {lastEstadoDeEjecucion?.estatus_de_ejecucion?.descripcion || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Actuación">
            {actuacionesPae.find((actuacion) => actuacion.id === lastEstadoDeEjecucion?.actuacion)?.descripcion || ' - '}
          </Descriptions.Item>
        </Descriptions>
      </Col>
      <Col xs={24} md={16}>
        <Form.Item
          name="observaciones"
          label="Observaciones de reporte moroso"
          normalize={(value) => capitalize(value)}
        >
          <Input.TextArea
            disabled={!mutable}
            allowClear
            autoSize={{ minRows: 3, maxRows: 6 }}
            maxLength={500}
          />
        </Form.Item>
      </Col>
      <br />
      <Col span={24}>
        <Typography.Title level={4}>Padrón</Typography.Title>
      </Col>
      <Row justify="end">
        <Col>
          <ModalJSON
            json={ejecucion?.datos_de_json_del_padron}
            label="padrón"
            disabled={!mutable}
          />
        </Col>
        <Col>
          <ModalObservaciones
            remote
            formItemName="observaciones_del_padron"
            ejecucionId={ejecucion?.id}
            callback={(observaciones_del_padron) => setEjecucion((prev) => ({
              ...prev,
              observaciones_del_padron,
            }))}
            label="Agregar observaciones al padrón"
            disabled={!mutable}
          />
        </Col>
      </Row>
      <Col span={24}>
        <Descriptions
          layout="vertical"
          size="small"
          bordered
          column={{
            xs: 1, sm: 2, md: 3, lg: 4,
          }}
          labelStyle={{ fontWeight: 'bold' }}
        >
          <Descriptions.Item label="Padrón ID" span={1}>
            {ejecucion?.object_id_padron || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Clave del padrón" span={1}>
            {ejecucion?.clave_del_padron || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Clave principal del padrón" span={1}>
            {ejecucion?.clave_principal_del_padron || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Estatus del padrón" span={1}>
            {ejecucion?.estatus_del_padron || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Clave secundaria del padrón" span={1}>
            {ejecucion?.clave_secundaria_del_padron || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Nombres completos del padrón" span={4} contentStyle={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {ejecucion?.nombres_completos_del_padron || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Código postal del padrón" span={1}>
            {ejecucion?.direccion_codigo_postal?.toUpperCase() || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Correo electrónico del padrón" span={1}>
            {ejecucion?.correo_electronico_del_padron || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Teléfono del padrón" span={2}>
            {ejecucion?.telefono_del_padron || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Dirección del padrón" span={2} contentStyle={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {ejecucion?.direccion_del_padron?.toUpperCase() || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Dirección de notificación del padrón" span={2} contentStyle={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {ejecucion?.direccion_de_notificacion_del_padron?.toUpperCase() || ' - '}
          </Descriptions.Item>
          {ejecucion?.observaciones_del_padron && (
          <Descriptions.Item label="Observaciones" span={4} contentStyle={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {ejecucion?.observaciones_del_padron || ' - '}
          </Descriptions.Item>
          )}
        </Descriptions>
      </Col>
      <br />
      <Col span={24}>
        <Typography.Title level={4}>Contribuyente</Typography.Title>
      </Col>
      <Row justify="end">
        <Col>
          <ModalJSON
            json={ejecucion?.datos_del_contribuyente_json}
            label="contribuyente"
            disabled={!mutable}
          />
        </Col>
        <Col>
          <ModalObservaciones
            remote
            disabled={!mutable}
            formItemName="observaciones_del_contribuyente"
            ejecucionId={ejecucion?.id}
            callback={(observaciones_del_contribuyente) => setEjecucion((prev) => ({
              ...prev,
              observaciones_del_contribuyente,
            }))}
            label="Agregar observaciones al contribuyente"
          />
        </Col>
      </Row>
      <Col span={24}>
        <Descriptions
          layout="vertical"
          size="small"
          bordered
          column={{
            xs: 1, sm: 2, md: 3, lg: 5,
          }}
          labelStyle={{ fontWeight: 'bold' }}
        >
          <Descriptions.Item label="ID del contribuyente" span={1}>
            {ejecucion?.contribuyente || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Clave del contribuyente" span={1}>
            {ejecucion?.clave_del_contribuyente || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Tipo de persona">
            {tiposDePersonas
              .find((tipo) => tipo.id === ejecucion?.tipo_de_persona)?.descripcion || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Nombre / Razón Social" contentStyle={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {ejecucion?.nombre_completo_del_contribuyente || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Estatus del contribuyente" span={1}>
            {ejecucion?.estatus_del_contribuyente || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="RFC">
            {ejecucion?.rfc_del_contribuyente || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="CURP">
            {ejecucion?.curp_del_contribuyente || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Fecha de constitución">
            {ejecucion?.fecha_de_constitucion || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Dirección" span={2} contentStyle={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {ejecucion?.direccion_del_contribuyente?.toUpperCase() || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Correo electrónico" span={1}>
            {ejecucion?.correo_electronico_del_contribuyente || ' - '}
          </Descriptions.Item>
          <Descriptions.Item label="Dirección de notificación" contentStyle={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {ejecucion?.direccion_de_notificacion_del_contribuyente?.toUpperCase() || ' - '}
          </Descriptions.Item>
          {ejecucion?.observaciones_del_contribuyente && (
          <Descriptions.Item label="Observaciones" span={4} contentStyle={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {ejecucion?.observaciones_del_contribuyente || ' - '}
          </Descriptions.Item>
          )}
        </Descriptions>
      </Col>
    </>
  );
};

export default ReadOnlyTabGeneral;

ReadOnlyTabGeneral.propTypes = {
  ejecucion: PropTypes.shape({
    id: PropTypes.number.isRequired,
    folio: PropTypes.string.isRequired,
    origen_del_credito: PropTypes.number.isRequired,
    fecha_de_creacion: PropTypes.string.isRequired,
    estatus_credito: PropTypes.string.isRequired,
    datos_del_contribuyente_json: PropTypes.shape({}),
    datos_de_json_del_padron: PropTypes.shape({}),
    clave_del_padron: PropTypes.string.isRequired,
    clave_principal_del_padron: PropTypes.string.isRequired,
    clave_secundaria_del_padron: PropTypes.string.isRequired,
    nombres_completos_del_padron: PropTypes.string.isRequired,
    telefono_del_padron: PropTypes.string.isRequired,
    estatus_del_padron: PropTypes.string.isRequired,
    direccion_codigo_postal: PropTypes.string.isRequired,
    direccion_del_padron: PropTypes.string.isRequired,
    object_id_padron: PropTypes.string.isRequired,
    correo_electronico_del_padron: PropTypes.string.isRequired,
    direccion_de_notificacion_del_padron: PropTypes.string.isRequired,
    observaciones_del_padron: PropTypes.string.isRequired,
    observaciones_del_contribuyente: PropTypes.string.isRequired,
    rfc_del_contribuyente: PropTypes.string.isRequired,
    curp_del_contribuyente: PropTypes.string.isRequired,
    fecha_de_constitucion: PropTypes.string.isRequired,
    lugar_de_constitucion: PropTypes.string.isRequired,
    direccion_del_contribuyente: PropTypes.string.isRequired,
    correo_electronico_del_contribuyente: PropTypes.string.isRequired,
    direccion_de_notificacion_del_contribuyente: PropTypes.string.isRequired,
    actuacion: PropTypes.string.isRequired,
    clave_del_contribuyente: PropTypes.string.isRequired,
    nombre_completo_del_contribuyente: PropTypes.string.isRequired,
    apellido_paterno_del_contribuyente: PropTypes.string.isRequired,
    apellido_materno_del_contribuyente: PropTypes.string.isRequired,
    id_del_contribuyente: PropTypes.string.isRequired,
    tipo_de_persona: PropTypes.string.isRequired,
    estatus_del_contribuyente: PropTypes.string.isRequired,
    contribuyente: PropTypes.string.isRequired,
    estados_de_ejecucion: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      descripcion: PropTypes.string.isRequired,
      actuacion: PropTypes.string.isRequired,
      fecha_de_creacion: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  origenesDeCredito: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    descripcion: PropTypes.string.isRequired,
  })).isRequired,
  setEjecucion: PropTypes.func.isRequired,
  mutable: PropTypes.bool.isRequired,
  estadosDeEjecucion: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    descripcion: PropTypes.string.isRequired,
    actuacion: PropTypes.string.isRequired,
    fecha_de_creacion: PropTypes.string.isRequired,
  })).isRequired,
  estatusDeEjecuciones: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    descripcion: PropTypes.string.isRequired,
  })).isRequired,
};
